import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { IUnitedSections } from 'constants/businessToolSections';
import { useBreakpoints } from 'hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getActiveSection, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useBusinessToolAiContext } from 'pages/BusinessToolPage/components/BusinessToolAiProvider';

import { ReactComponent as AIIcon } from 'assets/icons/AIIcon.svg';
import { ReactComponent as CheckCircle } from 'assets/icons/CheckCircleGreen.svg';

import { Button } from 'components/_basic/Button/Button';
import { DotsLoader } from 'components/_basic/DotsLoader';

import { getDocumentType } from 'helpers/getDocumentType';

import styles from './AIFormLoader.module.scss';

export const AIFormLoader: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();

  const activeSection = useBusinessToolStore(getActiveSection);
  const { loadingStates, isWholeDocumentLoading, cancelSectionAI, cancelWholeDocumentAI } = useBusinessToolAiContext();

  const type = useBusinessToolStore(getBusinessPlanType);
  const isOnePage = getDocumentType(type) === BUSINESS_PLAN_TYPES.ONE_PAGE;

  const isSectionLoading = loadingStates[activeSection];

  useEffect(() => {
    const layoutEl = document.getElementById('rootLayout') as HTMLElement;

    if (isWholeDocumentLoading && isMobile) {
      document.body.style.overflow = 'hidden';
      layoutEl.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      layoutEl.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'unset';
      layoutEl.style.overflow = 'auto';
    };
  }, [isWholeDocumentLoading, isMobile]);

  const handleStop = () => {
    if (isWholeDocumentLoading) {
      cancelWholeDocumentAI();
      return;
    }
    cancelSectionAI({ sectionKey: activeSection });
  };

  if (!isSectionLoading && !isWholeDocumentLoading) {
    return null;
  }

  const text = t(
    isOnePage ? 'ai.onePageGenerating' : isWholeDocumentLoading ? 'ai.businessPlanGenerating' : 'ai.chapterGenerating',
  );

  const sectionKeys = Object.keys(loadingStates) as IUnitedSections[];

  return (
    <Box className={styles.loaderContainer}>
      <Box className={styles.flex}>
        <Box className={styles.sticky}>
          <Box className={styles.report}>
            <AIIcon className={styles.icon} />
            <Typography variant="h3" className={styles.title}>
              {t('ai.generation')}
            </Typography>
            <Box textAlign="center">
              <Typography variant="body1">{t('previewLoader.pleaseWait')}</Typography>
              <Typography variant="body1" className={styles.text}>
                {text}
              </Typography>
            </Box>
          </Box>
          {isWholeDocumentLoading && (isOnePage || isMobile) && (
            <Box className={styles.sectionsList}>
              <DotsLoader className={styles.onePageLoaderDot} />
            </Box>
          )}
          {isWholeDocumentLoading && !isOnePage && !isMobile && (
            <Box className={styles.sectionsList}>
              {sectionKeys.map((key) => {
                const chapterName = t(`chapters.${key}`);
                const isLoading = loadingStates[key];

                return (
                  <Box key={key} className={styles.sectionItem}>
                    <Typography variant="subtitle1" className={styles.sectionTitle}>
                      {chapterName}
                    </Typography>
                    {isLoading ? <DotsLoader /> : <CheckCircle className={styles.checkIcon} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
      <Box className={styles.footer}>
        <Button
          color="inherit"
          variant="outlined"
          size="small"
          className={styles.button}
          onClick={handleStop}
          fullWidth={isMobile}
        >
          {t('ai.stopGeneration')}
        </Button>
      </Box>
    </Box>
  );
};
